import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { differenceInCalendarDays, format, startOfDay } from 'date-fns';
import type { FlowMetrics, FlowSales, MetricsChoice } from '@/services/dynamicFlowsService';
import { formatNumber, formatPercent, safeRate } from './format';

export const MAX_RANGE_DAYS = 90;

export interface Kpi {
  key: string;
  label: string;
  value: string;
  hint: string;
  highlight?: boolean;
}

// Recalcula as taxas a partir das contagens quando o BE não as manda (ou manda inválidas).
export function buildKpis(totals: Partial<FlowMetrics['totals']> | null | undefined, sales?: FlowSales): Kpi[] {
  const views = totals?.views ?? 0;
  const plays = totals?.plays ?? 0;
  const checkout = totals?.checkout_sessions ?? 0;
  const playRate = Number.isFinite(totals?.play_rate)
    ? (totals?.play_rate as number)
    : safeRate(plays, views);
  const conversion = Number.isFinite(totals?.conversion_proxy)
    ? (totals?.conversion_proxy as number)
    : safeRate(checkout, plays);

  return [
    { key: 'views', label: translateInterfaceText("Visualizações"), value: formatNumber(views), hint: translateInterfaceText("Carregaram o fluxo") },
    { key: 'plays', label: translateInterfaceText("Plays"), value: formatNumber(plays), hint: translateInterfaceText("Clicaram para assistir") },
    {
      key: 'play_rate',
      label: translateInterfaceText("Taxa de play"),
      value: formatPercent(playRate),
      hint: translateInterfaceText("Plays ÷ visualizações"),
    },
    {
      key: 'checkout',
      label: translateInterfaceText("Acessos ao checkout"),
      value: formatNumber(checkout),
      hint: translateInterfaceText("Sessões com saída de checkout"),
    },
    {
      key: 'conversion',
      label: translateInterfaceText("Taxa de acesso ao checkout"),
      value: formatPercent(conversion),
      hint: translateInterfaceText("Checkout ÷ plays. Não confirma a compra."),
      highlight: true,
    },
    { key: 'approved_sales', label: translateInterfaceText("Vendas aprovadas"), value: sales?.connected && sales.approved_sales != null ? formatNumber(sales.approved_sales) : '—', hint: translateInterfaceText("Compras aprovadas na integração do fluxo") },
    { key: 'revenue', label: translateInterfaceText("Receita atribuída"), value: sales?.connected && sales.revenue != null ? new Intl.NumberFormat(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' }).format(sales.revenue) : '—', hint: translateInterfaceText("Receita das compras aprovadas") },
    { key: 'sales_conversion', label: translateInterfaceText("Conversão em venda"), value: sales?.connected && sales.conversion_rate != null ? formatPercent(sales.conversion_rate) : '—', hint: translateInterfaceText("Vendas aprovadas ÷ plays do fluxo") },
  ];
}

export interface ChoiceGroup {
  node_id: string;
  node_label: string;
  choices: MetricsChoice[];
}

// Agrupa as opções por etapa, na ordem em que as etapas aparecem.
export function groupChoicesByStep(choices: MetricsChoice[]): ChoiceGroup[] {
  const groups = new Map<string, ChoiceGroup>();
  for (const choice of choices) {
    const group = groups.get(choice.node_id) || {
      node_id: choice.node_id,
      node_label: choice.node_label,
      choices: [],
    };
    group.choices.push(choice);
    groups.set(choice.node_id, group);
  }
  return [...groups.values()];
}

export const toApiDate = (date: Date) => format(date, 'yyyy-MM-dd');

export function rangeDays(range: { from: Date; to: Date }): number {
  return differenceInCalendarDays(startOfDay(range.to), startOfDay(range.from)) + 1;
}

export const isRangeAllowed = (range: { from: Date; to: Date }) => rangeDays(range) <= MAX_RANGE_DAYS;

// Vídeo mostrado primeiro no gráfico de retenção: o primeiro com plays.
export function pickInitialVideo(videos: { video_id: string; plays: number }[]): string | null {
  if (!videos.length) return null;
  return (videos.find((video) => video.plays > 0) || videos[0]).video_id;
}
